import { useEffect, useEffectEvent, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../cn';
import { IconButton } from './IconButton';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode | undefined;
  /** Action buttons, right-aligned. */
  footer?: ReactNode | undefined;
  /**
   * `compact` (default): one padded column, a 420 px panel, the body in subdued body-sm.
   * `sections`: a 640 px panel with a ruled head, a scrolling body and a ruled footer, for
   * forms (the design's Settings modal).
   */
  layout?: 'compact' | 'sections' | undefined;
  /** `sections` only: shown before the title, for example an `<IconSquare size="lg" />`. */
  icon?: ReactNode | undefined;
  /** `sections` only: one subdued line under the title. */
  description?: ReactNode | undefined;
  /** Class for the panel, for example a width. */
  className?: string | undefined;
}

/** Modal over a navy backdrop: shadow-6, --radius-lg, Esc and backdrop click close, focus returns to the opener. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  layout = 'compact',
  icon,
  description,
  className,
}: DialogProps) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const close = useEffectEvent(onClose);

  // Once per open: focus the panel, close on Escape, give focus back to the opener after.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    panel.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [open]);

  if (!open) return null;
  const sections = layout === 'sections';
  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className={cn(
        'fixed inset-0 flex items-center justify-center bg-surface-static-backdrop',
        sections ? 'z-[150] p-6' : 'z-[100] p-4',
      )}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'flex flex-col rounded-lg bg-surface-static-default shadow-6 outline-none',
          sections
            ? 'max-h-[calc(100vh-48px)] w-[min(640px,100%)] animate-[ui-in_.22s_ease_both]'
            : 'w-[420px] max-w-full gap-3 p-6',
          className,
        )}
      >
        {sections ? (
          <>
            <div className="flex items-center gap-3 border-b border-border-subdued px-6 pt-5 pb-4">
              {icon ?? null}
              <div className="min-w-0 flex-1">
                <div id={titleId} className="ds-h5 text-content-default">
                  {title}
                </div>
                {description ? (
                  <div className="mt-0.5 text-[12.5px] text-content-subdued">{description}</div>
                ) : null}
              </div>
              <IconButton name="x" size="sm" aria-label="Close" onClick={onClose} />
            </div>
            <div className="flex flex-1 flex-col gap-[18px] overflow-y-auto px-6 py-5">{children}</div>
            {footer ? (
              <div className="flex justify-end gap-2 border-t border-border-subdued px-6 py-3.5">
                {footer}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <span id={titleId} className="ds-h4 text-content-default">
                {title}
              </span>
              <IconButton name="x" size="sm" aria-label="Close" onClick={onClose} />
            </div>
            {children !== undefined ? (
              <div className="ds-body-sm text-content-subdued">{children}</div>
            ) : null}
            {footer ? <div className="mt-2 flex justify-end gap-2">{footer}</div> : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
